/** Thin wrappers over the Worker's JSON API. */
import type { Boxes } from "../../shared/srs";
import type { DeckId } from "../../shared/vocab";
import type { WordCount } from "../../shared/protocol";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
  ) {
    super(code);
    this.name = "ApiError";
  }
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    credentials: "same-origin",
    ...init,
    headers: {
      ...(init.body ? { "content-type": "application/json" } : {}),
      ...(init.headers ?? {}),
    },
  });

  if (!res.ok) {
    let code = "request-failed";
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) code = body.error;
    } catch {
      // non-JSON error body
    }
    throw new ApiError(res.status, code);
  }

  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

const body = (data: unknown): RequestInit => ({ method: "POST", body: JSON.stringify(data) });

/* ── auth ───────────────────────────────────────────────────────────── */

export async function fetchMe(): Promise<string> {
  try {
    const { email } = await call<{ email: string }>("/api/me");
    return email;
  } catch {
    return "";
  }
}

export const requestCode = (email: string, turnstileToken?: string) =>
  call<{ challengeId: string }>("/api/auth/request-code", body({ email, turnstileToken }));

export const verifyCode = (challengeId: string, code: string) =>
  call<{ email: string }>("/api/auth/verify", body({ challengeId, code }));

export const signOut = () => call<{ ok: true }>("/api/auth/signout", { method: "POST" });

/* ── progress ───────────────────────────────────────────────────────── */

export const fetchProgress = (deck: DeckId) =>
  call<{ boxes: Boxes }>(`/api/progress/${deck}`).then((r) => r.boxes);

export const pushProgress = (deck: DeckId, changes: Boxes) =>
  call<{ written: number }>(`/api/progress/${deck}`, {
    method: "PATCH",
    body: JSON.stringify({ changes }),
  });

export const migrateProgress = (payload: Partial<Record<DeckId, Boxes>>) =>
  call<{ adopted: DeckId[] }>("/api/progress/migrate", body(payload));

/* ── duel ───────────────────────────────────────────────────────────── */

export const createDuel = (playerId: string, wordCount: WordCount) =>
  call<{ code: string }>("/api/duel", body({ playerId, wordCount }));

/**
 * Fire-and-forget flush on pagehide. sendBeacon cannot set headers, which is
 * exactly why the session is a cookie rather than a bearer token.
 */
export function beaconProgress(deck: DeckId, changes: Boxes): void {
  if (Object.keys(changes).length === 0) return;
  try {
    const blob = new Blob([JSON.stringify({ changes })], { type: "application/json" });
    navigator.sendBeacon(`/api/progress/${deck}`, blob);
  } catch {
    // best effort
  }
}
